import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { resetFlightItemsFromCheckout, setFlightDateChangeStep } from 'actions/FlightActions'
import { FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'
import { goBack } from 'connected-react-router'
import { getFlightChangeDatesPageLocation } from 'checkout/lib/utils/flights/dateChangeUtils'

/**
 * Custom hook for handling back navigation in flight date change flow
 * Navigates through each step in reverse order
 *
 * Uses URL detection to handle cases where Redux step may not match actual page,
 * which can happen when navigating between different checkout pages.
 */
export function useFlightDateChangeBackNavigation() {
  const dispatch = useAppDispatch()
  const currentStep = useAppSelector(state => state.flights.flightsDateChange.currentStep)
  const currentPathname = useAppSelector(state => state.router.location.pathname)

  const handleBack = useCallback(() => {
    // Determine actual location based on URL pathname for reliable navigation
    const { isOnInsurancePage, isOnPurchasePage, isOnFlightChangePage } = getFlightChangeDatesPageLocation(currentPathname)

    // Handle back from purchase/review page
    if (isOnPurchasePage) {
      // Go back to previous page (insurance or flight selection)
      // Set step to INSURANCE_SELECT_DATES since that's where we'll land if there's insurance
      dispatch(setFlightDateChangeStep(FlightChangeDatesPageStep.INSURANCE_SELECT_DATES))
      dispatch(goBack())
      return
    }

    // Handle back from insurance page
    if (isOnInsurancePage) {
      // Go back to flight selection page
      dispatch(setFlightDateChangeStep(FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS))
      // Reset flight items from checkout, as user navigates back to the previous step
      dispatch(resetFlightItemsFromCheckout())
      dispatch(goBack())
      return
    }

    // Handle internal navigation within flight change page (same URL, different sub-steps)
    if (isOnFlightChangePage) {
      switch (currentStep) {
        case FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE:
          // If we're on the first step, go back to the previous page (my escapes)
          dispatch(goBack())
          break
        case FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE:
          // Go back to select flights to change
          dispatch(setFlightDateChangeStep(FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE))
          break
        case FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS:
          // Go back to select dates to change
          dispatch(setFlightDateChangeStep(FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE))
          // Reset flight items from checkout, as user navigates back to the previous step
          dispatch(resetFlightItemsFromCheckout())
          break
        default:
          // For any other step on this page, go to first step
          dispatch(setFlightDateChangeStep(FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE))
          break
      }
      return
    }

    // Fallback: use browser back
    dispatch(goBack())
  }, [currentStep, currentPathname, dispatch])

  return {
    handleBack,
    currentStep,
  }
}
